import { Descriptions, Grid, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type StandardApplicationModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/StandardApplicationModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import StandardApplicationModuleAction from './StandardApplicationModuleAction';
import StandardApplicationModuleDetailsInner from './StandardApplicationModuleDetailsInner';
import StandardApplicationModuleFooter from './StandardApplicationModuleFooter';
import { EditMode, HeaderTabs } from './type';

export type StandardApplicationModulePageProps = { module: StandardApplicationModuleWithPermissionsSpecsFragment };

const StandardApplicationModulePage = ({ module }: StandardApplicationModulePageProps) => {
    const { t } = useTranslation(['standardApplicationModuleDetails']);
    const [activeTab, setActiveTab] = useState(HeaderTabs.ModuleDetails);
    const [editMode, setEditMode] = useState(EditMode.ModuleDetails);
    const navigate = useNavigate();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('standardApplicationModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('standardApplicationModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('standardApplicationModuleDetails:content.agreement')}>
                {module.agreementsModule.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('standardApplicationModuleDetails:content.customer')}>
                {`${module.customerModule.displayName} - ${module.kycPresets
                    .map(kycPreset => kycPreset.displayName)
                    .join(', ')}`}
            </Descriptions.Item>
            {module.bankModule && (
                <Descriptions.Item label={t('standardApplicationModuleDetails:content.financing')}>
                    {module.bankModule.displayName}
                </Descriptions.Item>
            )}
            <Descriptions.Item label={t('standardApplicationModuleDetails:content.vehicle')}>
                {module.vehicleModule.displayName}
            </Descriptions.Item>

            {module.appointmentModule ? (
                <Descriptions.Item label={t('standardApplicationModuleDetails:content.appointment')}>
                    {module.appointmentModule.displayName}
                </Descriptions.Item>
            ) : null}

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    useEffect(() => {
        switch (editMode) {
            case EditMode.ModuleDetails:
                setActiveTab(HeaderTabs.ModuleDetails);
                break;

            default:
                break;
        }
    }, [editMode]);

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<StandardApplicationModuleAction module={module} permissions={permissions} />}
            footer={[<StandardApplicationModuleFooter editMode={editMode} setEditMode={setEditMode} />]}
            header={{
                footer: (
                    <Tabs
                        activeKey={activeTab}
                        onChange={(activeKey: HeaderTabs) => {
                            switch (activeKey) {
                                case 'moduleDetails':
                                    setEditMode(EditMode.ModuleDetails);
                                    break;

                                default:
                                    setEditMode(null);
                            }
                            setActiveTab(activeKey);
                        }}
                    >
                        <Tabs.TabPane
                            key={HeaderTabs.ModuleDetails}
                            tab={t('standardApplicationModuleDetails:tabs.moduleDetails.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.Vehicles}
                            tab={t('standardApplicationModuleDetails:tabs.vehicles.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.FinanceProducts}
                            tab={t('standardApplicationModuleDetails:tabs.financeProducts.title')}
                        />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md
                    ? t('standardApplicationModuleDetails:title', { name: module.displayName })
                    : module.displayName
            }
        >
            <StandardApplicationModuleDetailsInner
                activeTab={activeTab}
                editMode={editMode}
                module={module}
                setEditMode={setEditMode}
            />
        </ConsolePageWithHeader>
    );
};

export default StandardApplicationModulePage;
